/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import {
    IPredictionAnalysis,
    IPredictionListResponse,
    IPredictionChartDataResponse,
    IApplyPredictionResult,
    ISimulationResult,
    IScenarioInputs,
} from "../../utility/types/collectTypes";

/**
 * Stage 2J — Customer Prediction Analysis service layer.
 *
 * Self-contained API client (same convention as `collectIntelligenceService.ts`).
 * Auth bearer is injected by the global axios interceptor; the tenant header
 * (`x-business-id`) is passed explicitly here.
 *
 * READ endpoints are available to any authenticated member (Staff read-only).
 * MUTATING endpoints (generate / simulate / scenarios / apply) are gated to
 * OWNER/ADMIN at the server route layer — the UI mirrors this gating.
 */

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        headers: { ...(extra.headers || {}), "x-business-id": businessId },
    };
}

function pickError(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

// ── Generate ─────────────────────────────────────────────────────────────

export interface IGeneratePredictionPayload {
    customerId: string;
    invoiceId?: string;
    assumptions?: IScenarioInputs;
}

export const generatePrediction = async (
    businessId: string,
    payload: IGeneratePredictionPayload,
): Promise<IPredictionAnalysis> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/predictions/generate`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as IPredictionAnalysis;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to generate prediction."));
    }
};

export const predictFromRecommendation = async (
    businessId: string,
    recommendationId: string,
    invoiceId?: string,
): Promise<IPredictionAnalysis> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/predictions/from-recommendation`,
            { recommendationId, invoiceId },
            withBusiness(businessId),
        );
        return res.data?.data as IPredictionAnalysis;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to generate prediction."));
    }
};

// ── Read ─────────────────────────────────────────────────────────────────

export interface IListPredictionsQuery {
    customerId?: string;
    invoiceId?: string;
    page?: number;
    limit?: number;
}

export const listPredictions = async (
    businessId: string,
    query: IListPredictionsQuery = {},
): Promise<IPredictionListResponse> => {
    try {
        const params: Record<string, any> = {};
        if (query.customerId) params.customerId = query.customerId;
        if (query.invoiceId) params.invoiceId = query.invoiceId;
        if (query.page) params.page = query.page;
        if (query.limit) params.limit = query.limit;
        const res = await axios.get(
            `${API_BASE_URL}/collect/predictions`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IPredictionListResponse;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load predictions."));
    }
};

export const getPrediction = async (
    businessId: string,
    predictionId: string,
): Promise<IPredictionAnalysis> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/predictions/${predictionId}`,
            withBusiness(businessId),
        );
        return res.data?.data as IPredictionAnalysis;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load prediction."));
    }
};

export const getPredictionChartData = async (
    businessId: string,
    predictionId: string,
): Promise<IPredictionChartDataResponse> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/predictions/${predictionId}/chart-data`,
            withBusiness(businessId),
        );
        return res.data?.data as IPredictionChartDataResponse;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load prediction charts."));
    }
};

export const listCustomerPredictions = async (
    businessId: string,
    customerId: string,
    query: { page?: number; limit?: number } = {},
): Promise<IPredictionListResponse> => {
    try {
        const params: Record<string, any> = {};
        if (query.page) params.page = query.page;
        if (query.limit) params.limit = query.limit;
        const res = await axios.get(
            `${API_BASE_URL}/collect/customers/${customerId}/predictions`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IPredictionListResponse;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load customer predictions."));
    }
};

// ── What-if simulation / scenarios ───────────────────────────────────────

export const simulatePrediction = async (
    businessId: string,
    predictionId: string,
    inputs: IScenarioInputs,
): Promise<ISimulationResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/predictions/${predictionId}/simulate`,
            { inputs },
            withBusiness(businessId),
        );
        return res.data?.data as ISimulationResult;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to simulate scenario."));
    }
};

export const addScenario = async (
    businessId: string,
    predictionId: string,
    inputs: IScenarioInputs,
    name?: string,
): Promise<IPredictionAnalysis> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/predictions/${predictionId}/scenarios`,
            { inputs, name },
            withBusiness(businessId),
        );
        // Server returns { prediction, scenario }; the page only needs the
        // refreshed prediction document.
        return (res.data?.data?.prediction ??
            res.data?.data) as IPredictionAnalysis;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to save scenario."));
    }
};

export const updateScenario = async (
    businessId: string,
    predictionId: string,
    scenarioId: string,
    inputs: IScenarioInputs,
    name?: string,
): Promise<IPredictionAnalysis> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/predictions/${predictionId}/scenarios/${scenarioId}`,
            { inputs, name },
            withBusiness(businessId),
        );
        return (res.data?.data?.prediction ??
            res.data?.data) as IPredictionAnalysis;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update scenario."));
    }
};

// ── Apply ────────────────────────────────────────────────────────────────

export const applyPrediction = async (
    businessId: string,
    predictionId: string,
    applyUnderlyingRecommendation = false,
): Promise<IApplyPredictionResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/predictions/${predictionId}/apply`,
            { applyUnderlyingRecommendation },
            withBusiness(businessId),
        );
        return res.data?.data as IApplyPredictionResult;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to apply prediction."));
    }
};
